import { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { onePagersService } from '../services/onePagersService';

const PROVIDER_PLACEHOLDER = '{SERVICE PROVIDER ACCOUNT NAME}';

function buildEmailTemplate(providerName: string): string {
  const name = providerName.trim() || PROVIDER_PLACEHOLDER;
  return [
    `<p>{INSERT BRIEF PROVIDER DESCRIPTION FOLLOWED BY MOST RELEVANT FIT POINTS - I.E. number of successful engagements performed for other BluWave PE/Portco clients, high level description of most relevant project, relevant industry experience points, engagement feedback (ratings or qualitative feedback, etc)}</p>`,
    `<p><br></p>`,
    `<p>Next Steps: Upon review, please let us know what your availability looks like to meet with ${name}. If you have any questions about ${name} to inform your selection decision, please let us know, and we'll make sure they're addressed during the intro call.</p>`,
  ].join('');
}

function isQuillEmpty(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').trim() === '';
}

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

interface SendEmailModalProps {
  accountId: string;
  introductionId: string;
  versionId: string;
  conciergeId: string;
  providerName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function SendEmailModal({
  accountId,
  introductionId,
  versionId,
  conciergeId,
  providerName = '',
  onClose,
  onSuccess,
}: SendEmailModalProps) {
  const [body, setBody] = useState(() => buildEmailTemplate(providerName));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasPlaceholders = body.includes('{INSERT ');

  const handleSend = async () => {
    setError(null);
    setLoading(true);
    try {
      await onePagersService.sendEmail(accountId, introductionId, versionId, {
        conciergeId,
        emailBody: isQuillEmpty(body) ? undefined : body,
      });
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative mx-auto mt-24 flex w-full max-w-[640px] max-h-[calc(100vh-7rem)] flex-col rounded-md bg-white shadow-lg">

        {/* Header — sticky, never scrolls */}
        <div className="shrink-0 px-6 pt-6">
          <div className="text-base font-semibold text-gray-900">Send One-Pager Email</div>
          <p className="mt-2 text-sm text-gray-600">
            Recipients will get a project update email with this one-pager as a PDF version as well
            as a direct link to view it in their platform.
          </p>
          <div className="mt-4 flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
            <svg className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            A PDF of this one-pager will be included in the email.
          </div>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-5">
          <label className="block text-sm font-medium text-gray-700">Email Body</label>
          <div className="mt-1 overflow-hidden rounded-md border border-[rgba(0,0,0,0.15)] [&_.ql-container]:min-h-[220px] [&_.ql-container]:text-sm [&_.ql-editor]:min-h-[220px]">
            <ReactQuill
              theme="snow"
              value={body}
              onChange={setBody}
              modules={QUILL_MODULES}
              readOnly={loading}
            />
          </div>

          {error && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer — sticky, never scrolls */}
        <div className="shrink-0 flex justify-end gap-3 px-6 py-4">
          <button
            type="button"
            className="h-[32px] rounded-[8px] bg-white px-[13px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] border border-[rgba(0,0,0,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <span title={hasPlaceholders ? 'Placeholder text detected — please remove before sending' : undefined}>
            <button
              type="button"
              className="h-[32px] rounded-[8px] bg-[#29446D] px-[24px] text-center font-['Inter',sans-serif] text-[14px] font-bold leading-[20px] tracking-[-0.1504px] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSend}
              disabled={loading || hasPlaceholders}
            >
              {loading ? 'Sending…' : 'Send Email'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
